/**
 * ReconnectingWebSocket https://github.com/pladaria/reconnecting-websocket
 * 如果 websockt 连接出错被关闭，将自动重新连接 webSocket。
 */
import ReconnectingWebSocket, { ErrorEvent, CloseEvent, ListenersMap, Message } from 'reconnecting-websocket';

import { logMemory } from '@/common/processMonitor';
import { IS_BROWSER } from '@/common/constant/platform';
import i18n, { i18n_t } from '@/locales/i18n';
import { IpcEvents } from '@/ipc-events';
import { httpUrl } from '@/renderer/scripts/http';
import user from '@/renderer/scripts/utils/user';
import store from '@/renderer/store';
import { transformErrorForSerialization } from '@/common/tools';
import { MessageItem } from '@/renderer/scripts/libs/smh-sdk-class/typings';
import sysNotifications from '@/renderer/scripts/utils/SysNotifications';
import { NotificationsType } from '@/common/SysNotificationsDataDefine';
import ipc from '@/renderer/scripts/utils/ipc';
import { getMessageFullContent, getMessageShowTitle } from './MessageHelper';
class WebSocketManager {
  private wsUrl = `${httpUrl.wssSMHUrl}/user/v1/websocket`;
  private readonly heartBeatInterval = 30 * 1000;
  private heartBeatTimer = 0; // 发送心跳定时器
  private lastHeartbeatLogTime = 0; // 上一次打印心跳日志的时间
  private lastMemoryLogTime = 0; // 上一次打印内存日志的时间

  public ws: ReconnectingWebSocket | null = null;
  constructor() {
    this.ws = this.createConnect();
  }
  private sendHeartBeat = () => {
    if (this.ws) {
      // 一小时才打一次心跳日志
      if (this.lastHeartbeatLogTime === 0 || Math.abs(this.lastHeartbeatLogTime - Date.now()) > 60 * 60 * 1000) {
        logger.ui('[websocket] sendHeartBeat');
        this.lastHeartbeatLogTime = Date.now();
      }
      // 半小时打一次内存日志
      if (this.lastMemoryLogTime === 0 || Math.abs(this.lastMemoryLogTime - Date.now()) > 30 * 60 * 1000) {
        logMemory(logger.ui);
        this.lastMemoryLogTime = Date.now();
      }
      this.ws.send(
        JSON.stringify({
          type: 'heartBeat',
        }),
      );
    }
    window.clearTimeout(this.heartBeatTimer);
    this.heartBeatTimer = window.setTimeout(this.sendHeartBeat.bind(this), this.heartBeatInterval);
  };
  private handleMessage = (e: MessageEvent) => {
    const { type, data } = e;
    let message: {
      type: string;
      [key: string]: any;
    } = {
      type,
    };
    try {
      message = JSON.parse(data);
    } catch (error) {
      message = {
        type,
        data,
      };
    }
    return message;
  };
  private handleError = (e: ErrorEvent) => {
    const { type, error } = e;
    const message: Record<string, any> = {
      type,
    };
    message.error = JSON.stringify(transformErrorForSerialization(error));
    return message;
  };
  private getCorrectUrl() {
    const { isLogined, userToken } = user.getUserInfoSync();
    let device = 'pc';
    if (IS_BROWSER) {
      device = 'web';
    }
    if (isLogined && !this.wsUrl.includes('user_token') && userToken) {
      this.wsUrl = `${httpUrl.wssSMHUrl}/user/v1/websocket?user_token=${userToken}&device=${device}`;
    }
    if (!isLogined && this.wsUrl.includes('user_token')) {
      this.wsUrl = `${httpUrl.wssSMHUrl}/user/v1/websocket?device=${device}`;
    }
  }

  /**
   * @description 全局监听消息中心，并给予消息提示数字
   * @param {MessageEvent} e
   */
  // eslint-disable-next-line class-methods-use-this
  private handleNewMessage(data: { type: string; [key: string]: any }) {
    if (data.type === 'message') {
      logger.ui('handleNewMessage', data);
      store.dispatch('UPDATE_MESSAGE_NUMBER');
      if (data?.body?.offlineMessageCount > 0) {
        // 启动前的消息总数
        const title = i18n_t('您有{slot1}条新消息通知', {
          slot1: data?.body?.offlineMessageCount,
        });
        const body = i18n_t('点击立即查看');
        ipc.send(IpcEvents.SHOW_NOTIFICATIONS, {
          id: 'offlineMessageCount',
          title,
          subtitle: '',
          body,
        });
        return;
      }
      if (
        data?.body.id !== undefined &&
        data?.body.title !== undefined &&
        data?.body.content !== undefined &&
        data?.body.hasRead === false
      ) {
        // 将新消息通知用户
        const messageItem = data?.body as MessageItem;
        if (messageItem) {
          sysNotifications.showNotifications(
            NotificationsType.TYPE_WSMSG,
            getMessageShowTitle(messageItem),
            getMessageFullContent(messageItem),
            '',
            false,
            data?.body.id || '',
          );
        }
      }
    }
  }
  public createConnect() {
    this.getCorrectUrl();
    if (this.ws && this.ws.url === this.wsUrl) return this.ws;
    this.closeConnect();
    this.ws = new ReconnectingWebSocket(this.wsUrl);
    this.ws.onopen = () => {
      this.getCorrectUrl();
      if (this.ws && this.ws.url !== this.wsUrl) {
        this.createConnect(); // 如果url里的userToken不正确，则需要重新创建
        return;
      }
      /* i18n-ignore*/
      const retryMsg = this.ws?.retryCount ? `，重试连接第 ${this.ws?.retryCount} 次` : '';
      /* i18n-ignore*/
      logger.ui(`[websocket] 建立连接${retryMsg}`);
      this.sendHeartBeat();
    };
    this.ws.onmessage = (e: MessageEvent) => {
      const message = this.handleMessage(e);
      this.handleNewMessage(message);
      if (message.type === 'heartBeat') {
        // 发完心跳后10s内收到的心跳信息才打印
        if (Math.abs(this.lastHeartbeatLogTime - Date.now()) < 10 * 1000) {
          logger.ui('[websocket] 收到消息', JSON.stringify(message));
        }
      } else {
        logger.ui('[websocket] 收到消息', JSON.stringify(message));
      }
    };
    this.ws.onerror = (e: ErrorEvent) => {
      const error = this.handleError(e);
      /* i18n-ignore*/
      logger.ui('[websocket] 连接出错', JSON.stringify(error));
    };
    this.ws.onclose = (e: CloseEvent) => {
      const message = {
        code: e.code,
        reason: e.reason,
      };
      /* i18n-ignore*/
      logger.ui('[websocket] 连接关闭', JSON.stringify(message));
    };
    return this.ws;
  }
  addEventListener<T extends keyof ListenersMap>(type: T, listener: any) {
    if (this.ws) {
      this.ws.addEventListener(type, listener);
    } else {
      const ws = this.createConnect();
      ws.addEventListener(type, listener);
    }
  }
  removeEventListener<T extends keyof ListenersMap>(type: T, listener: any) {
    if (this.ws) {
      this.ws.removeEventListener(type, listener);
    }
  }
  send(params: Object | string) {
    let message: Message;
    try {
      message = JSON.stringify(params);
    } catch (error) {
      message = params as string;
    }
    /* i18n-ignore*/
    logger.ui('[websocket] 发送消息', message);
    if (this.ws) {
      this.ws.send(message);
    } else {
      const ws = this.createConnect();
      ws.send(message);
    }
  }
  public closeConnect() {
    this.ws && this.ws.close();
    this.ws = null;
    window.clearTimeout(this.heartBeatTimer);
    this.heartBeatTimer = 0;
  }
}
export default new WebSocketManager();
